import { Typography } from "antd";
import { DefaultButton } from "components/Shared/DefaultButton";
import { DefaultModal } from "components/Shared/DefaultModal";
import { useSubmitFixtureTask } from "hooks/useFixtureTaskApi";
import { useSubmitSeasonTask } from "hooks/useSeasonTaskApi";
import { useState } from "react";
import { useIntl } from "react-intl";

type Props = {
  entityId: string;
  season?: any;
  fixture?: any;
  roster: Array<any>;
  staff: Array<any>;
};

export const SubmitButton = ({ entityId, season, fixture, roster, staff }: Props) => {
  const [openModal, setOpenModal] = useState(false);
  const { formatMessage } = useIntl();
  const { mutateAsync: submitSeasonTask, isPending: isSeasonTaskSubmitting } = useSubmitSeasonTask();
  const { mutateAsync: submitFixtureTask, isPending: isFixtureTaskSubmitting } = useSubmitFixtureTask();

  const validationFailed =
    (roster && fixture?.maximumRosterSize && roster.filter((x) => x.isActive).length > fixture.maximumRosterSize) ||
    (roster &&
      fixture?.maximumRosterStarterSize &&
      roster.filter((x) => x.isStarter).length !== fixture.maximumRosterStarterSize) ||
    staff?.some((x) => x.isActive && !x.role);

  const disabled = fixture ? !fixture.areRosterAndStaffEditable || validationFailed : !season.areRosterAndStaffEditable;

  const onSubmit = async () => {
    if (fixture) {
      await submitFixtureTask({
        entityId: entityId,
        seasonId: fixture.seasonId,
        fixtureId: fixture.fixtureId,
      });
    } else if (season) {
      await submitSeasonTask({ entityId: entityId, seasonId: season.seasonId });
    }

    setOpenModal(false);
  };

  return (
    <>
      <DefaultButton
        disabled={disabled}
        onClick={() => setOpenModal(true)}
        icon={<i className="fa-solid fa-paper-plane"></i>}
      >
        {formatMessage({ id: "button.submit" })}
      </DefaultButton>
      {openModal && (
        <DefaultModal
          width={undefined}
          open={openModal}
          onOk={onSubmit}
          title={formatMessage(
            { id: "submitConfirmationModal.title" },
            {
              objectType: formatMessage({
                id: fixture ? "objectType.fixture" : "objectType.season",
              }),
            }
          )}
          onCancel={() => setOpenModal(false)}
          cancelText={formatMessage({ id: "button.no" })}
          okButtonProps={{
            loading: isSeasonTaskSubmitting || isFixtureTaskSubmitting,
          }}
          cancelButtonProps={{
            disabled: isSeasonTaskSubmitting || isFixtureTaskSubmitting,
          }}
          okText={formatMessage({ id: "button.yes" })}
        >
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            {formatMessage(
              { id: "submitConfirmationModal.content.line1" },
              {
                objectType: formatMessage({
                  id: fixture ? "objectType.fixture" : "objectType.season",
                }),
              }
            )}
          </Typography.Paragraph>
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            {formatMessage({ id: "submitConfirmationModal.content.line2" })}
          </Typography.Paragraph>
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            {formatMessage({ id: "submitConfirmationModal.content.line3" })}
          </Typography.Paragraph>
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            {formatMessage({ id: "submitConfirmationModal.content.line4" })}
          </Typography.Paragraph>
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            {formatMessage({ id: "submitConfirmationModal.content.line5" })}
          </Typography.Paragraph>
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            {formatMessage({ id: "submitConfirmationModal.content.line6" })}
          </Typography.Paragraph>
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            {formatMessage({ id: "submitConfirmationModal.content.line7" })}
          </Typography.Paragraph>
        </DefaultModal>
      )}
    </>
  );
};
